/**
 * Icons: vector glyphs drawn into a Pixi.Graphics. Two flavours:
 *  - `drawPauseIcon` for big pause-menu tiles (scalable via half-size `r`)
 *  - `drawSmallIcon` for the fixed 16-ish px white icons used on the
 *    win-overlay button row and the hamburger restore button
 *
 * Stateless and Pixi-only — no DOM, no scene refs.
 */

import { Graphics } from 'pixi.js';

export type PauseIcon =
  | 'play' | 'restart' | 'next' | 'levels' | 'jukebox' | 'replay'
  | 'musicOn' | 'musicOff' | 'sfxOn' | 'sfxOff'
  | 'fsOn' | 'fsOff' | 'edit' | 'home' | 'back' | 'folder' | 'customize'
  | 'stats' | 'input';

export type SmallIcon = 'replay' | 'save' | 'copy' | 'load' | 'menu' | 'share' | 'play';

/** Draw a pause-menu icon centred on (cx,cy) with a half-size of `r`. */
export function drawPauseIcon(g: Graphics, kind: PauseIcon, cx: number, cy: number, r: number, color: number): void {
  const sw = Math.max(1.5, r * 0.18);
  const stroke = { color, width: sw, alpha: 1, cap: 'round' as const, join: 'round' as const };
  switch (kind) {
    case 'play':
      g.poly([cx - r * 0.55, cy - r, cx + r * 0.85, cy, cx - r * 0.55, cy + r]).fill(color);
      break;
    case 'restart': {
      const start = -Math.PI * 0.25, end = Math.PI * 1.25;
      const steps = 24;
      const pts: number[] = [];
      for (let i = 0; i <= steps; i++) {
        const a = start + (end - start) * (i / steps);
        pts.push(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
      }
      g.poly(pts, false).stroke(stroke);
      const ex = cx + Math.cos(end) * r, ey = cy + Math.sin(end) * r;
      g.poly([ex, ey, ex - r * 0.35, ey - r * 0.05, ex - r * 0.05, ey + r * 0.4]).fill(color);
      break;
    }
    case 'next':
      g.poly([cx - r, cy - r * 0.8, cx + r * 0.05, cy, cx - r, cy + r * 0.8]).fill(color);
      g.poly([cx + r * 0.05, cy - r * 0.8, cx + r * 0.95, cy, cx + r * 0.05, cy + r * 0.8]).fill(color);
      g.rect(cx + r * 0.95, cy - r * 0.8, sw * 0.9, r * 1.6).fill(color);
      break;
    case 'levels': {
      const s = r * 0.55;
      for (let row = 0; row < 2; row++) for (let col = 0; col < 3; col++) {
        g.roundRect(cx - r + col * (s + 4), cy - r * 0.6 + row * (s + 4), s, s, 2).fill(color);
      }
      break;
    }
    case 'jukebox': {
      const bars = [0.5, 0.9, 0.35, 0.75, 0.55];
      const bw = (r * 1.8) / (bars.length * 1.6);
      bars.forEach((bh, i) => {
        const x = cx - r + i * bw * 1.6;
        const h = r * bh * 1.4;
        g.roundRect(x, cy + r - h, bw, h, bw / 2).fill(color);
      });
      break;
    }
    case 'replay': {
      g.roundRect(cx - r, cy - r * 0.85, r * 2, r * 1.7, 4).stroke(stroke);
      for (let i = 0; i < 3; i++) {
        g.rect(cx - r + 3 + i * (r * 0.62), cy - r * 0.85 + 3, r * 0.18, r * 0.18).fill(color);
        g.rect(cx - r + 3 + i * (r * 0.62), cy + r * 0.85 - 3 - r * 0.18, r * 0.18, r * 0.18).fill(color);
      }
      g.poly([cx - r * 0.25, cy - r * 0.4, cx + r * 0.5, cy, cx - r * 0.25, cy + r * 0.4]).fill(color);
      break;
    }
    case 'musicOn':
    case 'musicOff': {
      g.rect(cx - r * 0.12, cy - r, sw * 1.4, r * 1.7).fill(color);
      g.circle(cx - r * 0.4, cy + r * 0.65, r * 0.4).fill(color);
      g.poly([cx - r * 0.12 + sw * 1.4, cy - r, cx + r * 0.7, cy - r * 0.55, cx + r * 0.7, cy - r * 0.15, cx - r * 0.12 + sw * 1.4, cy - r * 0.6]).fill(color);
      if (kind === 'musicOff') {
        g.poly([cx - r, cy + r, cx + r, cy - r]).stroke({ color: 0xff5566, width: sw * 1.2, cap: 'round' });
      }
      break;
    }
    case 'sfxOn':
    case 'sfxOff': {
      g.poly([cx - r, cy - r * 0.4, cx - r * 0.3, cy - r * 0.4, cx + r * 0.3, cy - r, cx + r * 0.3, cy + r, cx - r * 0.3, cy + r * 0.4, cx - r, cy + r * 0.4]).fill(color);
      if (kind === 'sfxOn') {
        for (let i = 1; i <= 2; i++) {
          const rad = r * (0.45 + i * 0.3);
          const steps = 10;
          const pts: number[] = [];
          for (let k = 0; k <= steps; k++) {
            const a = -Math.PI * 0.35 + (Math.PI * 0.7) * (k / steps);
            pts.push(cx + r * 0.45 + Math.cos(a) * rad, cy + Math.sin(a) * rad);
          }
          g.poly(pts, false).stroke({ color, width: sw, cap: 'round' });
        }
      } else {
        g.poly([cx + r * 0.45, cy - r * 0.55, cx + r, cy + r * 0.05]).stroke({ color, width: sw, cap: 'round' });
        g.poly([cx + r, cy - r * 0.55, cx + r * 0.45, cy + r * 0.05]).stroke({ color, width: sw, cap: 'round' });
      }
      break;
    }
    case 'fsOn':
    case 'fsOff': {
      const inset = kind === 'fsOff' ? r * 0.35 : 0;
      const arm = r * 0.55;
      const corners = [
        [cx - r + inset, cy - r + inset, 1, 1],
        [cx + r - inset, cy - r + inset, -1, 1],
        [cx - r + inset, cy + r - inset, 1, -1],
        [cx + r - inset, cy + r - inset, -1, -1],
      ];
      for (const [x, y, dx, dy] of corners) {
        g.poly([x! + dx! * arm, y!, x!, y!, x!, y! + dy! * arm]).stroke({ color, width: sw, cap: 'round', join: 'round' });
      }
      break;
    }
    case 'edit': {
      g.poly([cx - r, cy + r, cx + r * 0.55, cy - r * 0.7, cx + r, cy - r * 0.25, cx - r * 0.55, cy + r * 1.45]).stroke(stroke);
      g.poly([cx - r, cy + r, cx - r * 0.4, cy + r * 1.1, cx - r * 0.55, cy + r * 1.45]).fill(color);
      break;
    }
    case 'home': {
      g.poly([cx, cy - r, cx + r, cy - r * 0.05, cx + r, cy + r, cx - r, cy + r, cx - r, cy - r * 0.05]).stroke(stroke);
      g.rect(cx - r * 0.3, cy + r * 0.1, r * 0.6, r * 0.9).fill(color);
      break;
    }
    case 'back': {
      g.poly([cx + r * 0.6, cy - r, cx - r * 0.4, cy, cx + r * 0.6, cy + r]).stroke(stroke);
      g.rect(cx - r * 0.4, cy - sw * 0.5, r * 1.4, sw).fill(color);
      break;
    }
    case 'folder': {
      g.poly([cx - r, cy - r * 0.55, cx - r * 0.2, cy - r * 0.55, cx + r * 0.05, cy - r * 0.85, cx + r, cy - r * 0.85, cx + r, cy + r * 0.7, cx - r, cy + r * 0.7]).fill(color);
      g.rect(cx - r, cy - r * 0.4, r * 2, r * 1.1).fill(color);
      break;
    }
    case 'customize': {
      // T-shirt silhouette: small head circle + shirt body with sleeves.
      g.circle(cx, cy - r * 0.7, r * 0.28).fill(color);
      g.poly([
        cx - r * 0.95, cy - r * 0.05,
        cx - r * 0.45, cy - r * 0.45,
        cx - r * 0.18, cy - r * 0.45,
        cx + r * 0.18, cy - r * 0.45,
        cx + r * 0.45, cy - r * 0.45,
        cx + r * 0.95, cy - r * 0.05,
        cx + r * 0.6,  cy + r * 0.15,
        cx + r * 0.6,  cy + r * 0.85,
        cx - r * 0.6,  cy + r * 0.85,
        cx - r * 0.6,  cy + r * 0.15,
      ]).fill(color);
      break;
    }
    case 'stats': {
      // Bar chart: three bars of increasing height with a baseline.
      const bw = r * 0.42;
      const gap = r * 0.18;
      const baseY = cy + r * 0.85;
      const heights = [r * 0.7, r * 1.1, r * 1.55];
      for (let i = 0; i < 3; i++) {
        const x = cx - (bw * 1.5 + gap) + i * (bw + gap);
        const h = heights[i]!;
        g.rect(x, baseY - h, bw, h).fill(color);
      }
      g.rect(cx - r, baseY, r * 2, sw).fill(color);
      break;
    }
    case 'input': {
      // Gamepad body (rounded rect) with a small WiFi arc above.
      const bodyW = r * 1.8, bodyH = r * 1.1;
      const bx = cx - bodyW / 2, by = cy - bodyH / 2 + r * 0.25;
      g.roundRect(bx, by, bodyW, bodyH, r * 0.35).fill(color);
      // D-pad cross on the left half.
      const dpx = cx - r * 0.65, dpy = cy + r * 0.25;
      const ds = sw * 1.5;
      g.rect(dpx - ds, dpy - r * 0.28, ds * 2, r * 0.56).fill(0);
      g.rect(dpx - r * 0.28, dpy - ds * 0.75, r * 0.56, ds * 1.5).fill(0);
      // Two face buttons on the right half.
      g.circle(cx + r * 0.5, cy + r * 0.15, sw * 1.3).fill(0);
      g.circle(cx + r * 0.78, cy + r * 0.35, sw * 1.3).fill(0);
      // WiFi arc (two arcs) above the body — indicates wireless.
      for (let i = 1; i <= 2; i++) {
        const rad = r * (0.2 + i * 0.22);
        const pts: number[] = [];
        const steps = 10;
        for (let k = 0; k <= steps; k++) {
          const a = Math.PI + (Math.PI * 0.55) * (k / steps);
          pts.push(cx + Math.cos(a) * rad, by - r * 0.12 + Math.sin(a) * rad);
        }
        g.poly(pts, false).stroke({ color, width: sw * 0.9, cap: 'round' });
      }
      g.circle(cx, by - r * 0.12, sw * 0.75).fill(color);
      break;
    }
  }
}

/** Draws a 16px-ish white icon centered at (cx, cy) using only Graphics
 *  primitives so we don't need any SVG/texture pipeline. */
export function drawSmallIcon(kind: SmallIcon, cx: number, cy: number): Graphics {
  const g = new Graphics();
  const stroke = { color: 0xffffff, width: 2, alpha: 0.95 } as const;
  switch (kind) {
    case 'replay': {
      // 270° refresh arc: starts at top, sweeps clockwise through
      // right + bottom, ends at the left side. Arrowhead at the end
      // point pointing up (continuing the clockwise tangent), giving the
      // standard "refresh" / "replay" silhouette.
      const r = 6;
      g.arc(cx, cy, r, -Math.PI / 2, Math.PI).stroke(stroke);
      const ex = cx - r, ey = cy;
      g.moveTo(ex - 3, ey + 1).lineTo(ex, ey - 3).lineTo(ex + 3, ey + 1).stroke(stroke);
      break;
    }
    case 'save': {
      g.moveTo(cx, cy - 6).lineTo(cx, cy + 3).stroke(stroke);
      g.moveTo(cx - 4, cy - 1).lineTo(cx, cy + 3).lineTo(cx + 4, cy - 1).stroke(stroke);
      g.moveTo(cx - 6, cy + 6).lineTo(cx + 6, cy + 6).stroke(stroke);
      break;
    }
    case 'copy': {
      g.roundRect(cx - 6, cy - 4, 9, 9, 1.5).stroke(stroke);
      g.roundRect(cx - 3, cy - 7, 9, 9, 1.5).stroke(stroke);
      break;
    }
    case 'load': {
      g.roundRect(cx - 7, cy - 3, 14, 9, 1.5).stroke(stroke);
      g.moveTo(cx - 7, cy - 3).lineTo(cx - 4, cy - 6).lineTo(cx - 1, cy - 6).lineTo(cx + 1, cy - 3).stroke(stroke);
      break;
    }
    case 'menu': {
      g.moveTo(cx - 7, cy - 5).lineTo(cx + 7, cy - 5).stroke(stroke);
      g.moveTo(cx - 7, cy    ).lineTo(cx + 7, cy    ).stroke(stroke);
      g.moveTo(cx - 7, cy + 5).lineTo(cx + 7, cy + 5).stroke(stroke);
      break;
    }
    case 'share': {
      // Three nodes (origin + two arms) joined by lines — the classic
      // share-graph glyph.
      g.circle(cx - 5, cy + 4, 2).stroke(stroke);
      g.circle(cx + 5, cy - 4, 2).stroke(stroke);
      g.circle(cx + 5, cy + 4, 2).stroke(stroke);
      g.moveTo(cx - 3, cy + 3).lineTo(cx + 3, cy - 3).stroke(stroke);
      g.moveTo(cx - 3, cy + 5).lineTo(cx + 3, cy + 4).stroke(stroke);
      break;
    }
    case 'play': {
      // Filled right-pointing triangle — universal "play" glyph; used to
      // exit replay playback and start a fresh live run of this level.
      g.moveTo(cx - 4, cy - 6).lineTo(cx + 6, cy).lineTo(cx - 4, cy + 6).closePath()
        .fill({ color: 0xffffff, alpha: 0.95 });
      break;
    }
  }
  return g;
}
